// Typed wrappers around the Directory endpoints. Types come from
// `./generated/schema.ts` (regenerated via `pnpm generate`); the runtime is
// the shared @my/platform/http transport so bearer token, X-App-Key,
// X-Idempotency-Key, and Result<T> error mapping all behave identically to
// every other call in the suite.
import { request } from '@my/platform/http';
import type {
  components,
  AddressTypeValue,
  AppNameValue,
  AppRoleValue,
  BaseStatusValue,
  SiteTypeValue,
} from './generated/schema';

// Re-export the enum value unions so app code can spell types like
// `AccountingSystemValue` directly without reaching into `/generated`.
export type {
  AccountingSystemValue,
  AccountUserRoleValue,
  AccountUserStatusValue,
  AddressTypeValue,
  AppNameValue,
  AppRoleValue,
  BaseStatusValue,
  EquipmentClassValue,
  InvoiceTypeValue,
  NominalTypeValue,
  SequenceFormatValue,
  SequenceTypeValue,
  SiteTypeValue,
  TaxTypeValue,
  VatRoundingMethodValue,
} from './generated/schema';

/** `/directory/api/v1/info/api-info` response shape. */
export type ApiInfo = components['schemas']['ApiInfoResponse'];

/**
 * Organisation read model. Flat shape mirroring the real `OrganisationDto`
 * — note `mainOfficeAddressId` is just a Guid pointer; the address itself
 * is fetched via `getAddress(id)`.
 *
 * <p>
 * `tmsOrderReferenceLabelsJson` is layered in here until the OpenAPI
 * schema regen catches up — the field exists on the BE
 * `OrganisationResponse` already (it's how the TMS order detail page
 * resolves the per-org names of `Order.Reference1..4` slots), but the
 * generated `schema.ts` is stale. Same pattern as the `Address`
 * `fullAddress` / `placeId` override above.
 * </p>
 */
export type Organisation = components['schemas']['OrganisationResponse'] & {
  /**
   * JSON object string keyed by slot name (e.g.
   * `{ "Reference1": "Customer PO", "Reference2": "Internal Job" }`).
   * `null` / missing when the operator hasn't configured the labels yet
   * — TMS falls back to "Reference 1", "Reference 2", … in that case.
   */
  tmsOrderReferenceLabelsJson?: string | null;
};

/** PUT `/organisations` body. */
export type UpdateOrganisationRequest = components['schemas']['UpdateOrganisationRequest'];

/** PUT `/organisations/{id}/additional-currencies` body. */
export type UpdateOrganisationAdditionalCurrenciesRequest =
  components['schemas']['UpdateOrganisationAdditionalCurrenciesRequest'];

/**
 * PUT `/organisations/{id}/tms-order-reference-labels` body. The BE
 * validates that `labelsJson` is either `null` (clears the column) or
 * a well-formed JSON OBJECT — bare strings / arrays / primitives are
 * rejected with 422.
 */
export interface UpdateOrganisationTmsOrderReferenceLabelsRequest {
  labelsJson: string | null;
}

/** Address read model returned by `/addresses/{id}`. */
/**
 * Address read shape. Layered on top of the generated
 * `AddressResponse`: the structured `address1` / `address2` fields
 * have been collapsed into a single Google-formatted `fullAddress`
 * plus a `placeId` for re-fetch / dedup. We use `Omit` to drop the
 * stale fields and intersect the new ones until the schema is
 * regenerated against the post-migration BE. `partyName` / `partyType`
 * remain list-context extensions populated by the BE inline join.
 */
export type Address = Omit<
  components['schemas']['AddressResponse'],
  'address1' | 'address2'
> & {
  fullAddress: string;
  placeId: string;
  partyName?: string | null;
  partyType?: PartyTypeValue | null;
};

/** PUT `/addresses` body. */
export type UpdateAddressRequest = Omit<
  components['schemas']['UpdateAddressRequest'],
  'address1' | 'address2'
> & {
  fullAddress: string;
  placeId: string;
};

/** POST `/addresses` body. */
export type CreateAddressRequest = Omit<
  components['schemas']['CreateAddressRequest'],
  'address1' | 'address2'
> & {
  fullAddress: string;
  placeId: string;
};

/**
 * POST `/addresses` body for port / terminal Location records auto-created
 * from the route catalogue when an operator picks a booking route.
 *
 * The BE validator waives the Google Places requirement (`fullAddress` /
 * `placeId`) for `Location` type addresses, so all geocoding fields below
 * are optional: when the booking form can resolve the carrier's terminal
 * via Google Places it sends `code` + coordinates + `fullAddress` +
 * `placeId`; when the lookup fails it falls back to a bare name + country
 * row (legacy behaviour) and the BE still accepts it.
 *
 * `code` is the per-carrier terminal code `"{carrierCode}-{portCode}"`
 * (e.g. `"IF-DUB"`) — distinct per carrier so two carriers serving the
 * same physical port each get their own Location row.
 */
export interface CreateLocationAddressRequest {
  organisationId: string;
  addressType: 'Location';
  name: string;
  /** `"{carrierCode}-{portCode}"`, e.g. `"IF-DUB"`. */
  code?: string | null;
  city?: string | null;
  postcode?: string | null;
  countryIsoCode: string;
  latitude?: number | null;
  longitude?: number | null;
  /** Google `formatted_address` when geocoded; omitted on the fallback path. */
  fullAddress?: string | null;
  /** Google `place_id` when geocoded; omitted on the fallback path. */
  placeId?: string | null;
  status?: BaseStatusValue;
}

/** POST `/sites` body. */
export type CreateSiteRequest = components['schemas']['CreateSiteRequest'];

/**
 * PUT `/sites` body. Hand-mirrored from the BE `UpdateSiteCommand`
 * (see `My.Directory.Application/Features/Sites/UpdateSite`) because
 * the current generated swagger snapshot pre-dates the endpoint being
 * exposed. Once the schema is regenerated this can collapse back to
 * `components['schemas']['UpdateSiteCommand']`.
 */
export interface UpdateSiteRequest {
  siteId: string;
  name: string;
  type?: SiteTypeValue;
  parentId?: string | null;
  addressId: string;
  capacity?: number;
  amenities?: string | null;
  status?: BaseStatusValue;
}

/** Tax-rate row returned from `GET /organisationTaxRates`. */
export type OrganisationTaxRate = components['schemas']['OrganisationTaxRateResponse'];

/** POST `/organisationTaxRates` body. */
export type CreateOrganisationTaxRateRequest =
  components['schemas']['CreateOrganisationTaxRateRequest'];

/** PUT `/organisationTaxRates` body. */
export type UpdateOrganisationTaxRateRequest =
  components['schemas']['UpdateOrganisationTaxRateRequest'];

/** POST `/organisationTaxRates/seed-defaults` body. */
export type SeedOrganisationTaxRatesRequest =
  components['schemas']['SeedOrganisationTaxRatesRequest'];

/** Nominal-code row returned from `GET /organisationNominals`. */
export type OrganisationNominal = components['schemas']['OrganisationNominalResponse'];

/** POST `/organisationNominals` body. */
export type CreateOrganisationNominalRequest =
  components['schemas']['CreateOrganisationNominalRequest'];

/** PUT `/organisationNominals` body. */
export type UpdateOrganisationNominalRequest =
  components['schemas']['UpdateOrganisationNominalRequest'];

/**
 * Static equipment catalogue payload returned from `GET /equipmentCatalogue`.
 * Two-level shape: classes (Trailer, Rigid) → groups → types. The catalogue
 * is the same for every tenant; cache it long.
 */
export type EquipmentCatalogue = components['schemas']['EquipmentCatalogueResponse'];

/** One class entry inside `EquipmentCatalogue` (Trailer or Rigid). */
export type EquipmentCatalogueClass = components['schemas']['EquipmentCatalogueClassResponse'];

/** One group of related types within a class (e.g. "Reefer & Temp"). */
export type EquipmentCatalogueGroup = components['schemas']['EquipmentCatalogueGroupResponse'];

/** One picker-able catalogue type — what the user toggles in the dialog. */
export type EquipmentCatalogueType = components['schemas']['EquipmentCatalogueTypeResponse'];

/**
 * Per-org equipment-type selection row returned from
 * `GET /organisationEquipmentTypes`. The chip label is rendered by looking
 * up `catalogueKey` against `EquipmentCatalogue`.
 */
export type OrganisationEquipmentType =
  components['schemas']['OrganisationEquipmentTypeResponse'];

/** PUT `/organisationEquipmentTypes` body — replaces the selection for one class. */
export type ReplaceOrganisationEquipmentTypesRequest =
  components['schemas']['ReplaceOrganisationEquipmentTypesRequest'];

/**
 * Static carrier catalogue payload from `GET /carrierCatalogue`. Curated
 * server-side JSON — same for every tenant — so cache aggressively (24h).
 * Keys are append-only / never reused, so existing org selections never
 * dangle.
 */
export type CarrierCatalogue = components['schemas']['CarrierCatalogueResponse'];

/** One carrier in `CarrierCatalogue`. */
export type CarrierCatalogueEntry = components['schemas']['CarrierCatalogueEntryResponse'];

/**
 * Per-org carrier selection row from `GET /organisationCarriers`. The chip
 * label is rendered by looking up `catalogueKey` against the catalogue;
 * `partyId` points at the auto-created (or pre-existing) Supplier party.
 */
export type OrganisationCarrier = components['schemas']['OrganisationCarrierResponse'];

/** PUT `/organisationCarriers` body — replaces the org's full carrier set. */
export type ReplaceOrganisationCarriersRequest =
  components['schemas']['ReplaceOrganisationCarriersRequest'];

/**
 * Static shipping-routes catalogue from `GET /routeCatalogue`. Each entry
 * references its primary `carrierKey` so the FE can resolve the operator
 * label against `CarrierCatalogue` in the same render.
 */
export type RouteCatalogue = components['schemas']['RouteCatalogueResponse'];

/** One route in `RouteCatalogue`. */
export type RouteCatalogueEntry = components['schemas']['RouteCatalogueEntryResponse'];

/** Per-org route selection row from `GET /organisationRoutes`. */
export type OrganisationRoute = components['schemas']['OrganisationRouteResponse'];

/** PUT `/organisationRoutes` body — replaces the org's full route set. */
export type ReplaceOrganisationRoutesRequest =
  components['schemas']['ReplaceOrganisationRoutesRequest'];

/** Bank account row returned from `GET /organisationBankAccounts`. */
export type OrganisationBankAccount = components['schemas']['OrganisationBankAccountResponse'];

/** POST `/organisationBankAccounts` body. */
export type CreateOrganisationBankAccountRequest =
  components['schemas']['CreateOrganisationBankAccountRequest'];

/** PUT `/organisationBankAccounts` body. */
export type UpdateOrganisationBankAccountRequest =
  components['schemas']['UpdateOrganisationBankAccountRequest'];

/**
 * One numbering sequence per organisation per `SequenceType` (Invoice,
 * CreditNote, …). Returned from GET `/organisationSequences?organisationId`.
 */
export type OrganisationSequence = components['schemas']['OrganisationSequenceResponse'];

/** POST `/organisationSequences` body. */
export type CreateOrganisationSequenceRequest =
  components['schemas']['CreateOrganisationSequenceRequest'];

/** PUT `/organisationSequences` body. */
export type UpdateOrganisationSequenceRequest =
  components['schemas']['UpdateOrganisationSequenceRequest'];

/** POST `/organisationSequences/consume` body. */
export type ConsumeOrganisationSequenceRequest =
  components['schemas']['ConsumeOrganisationSequenceRequest'];

/** Return shape from `POST /organisationSequences/consume`. */
export type ConsumeOrganisationSequenceResponse =
  components['schemas']['ConsumeOrganisationSequenceResponse'];

/**
 * Result returned from `POST /organisations/{id}/logo`. The bare blob name
 * (`logo`) is rarely needed by the UI — it's exposed so callers writing back
 * to a TanStack-Query cache can keep the read-model perfectly in sync. The
 * `logoUrl` is the signed URL you actually render.
 */
export type UploadOrganisationLogoResponse =
  components['schemas']['UploadOrganisationLogoResponse'];

/**
 * Account returned by `/directory/api/v1/accounts/available`. The BE-generated
 * schema is still called `TenantResponse` (legacy naming on the contract);
 * the FE-facing alias uses the canonical "account" vocabulary used everywhere
 * else in the codebase. When the BE schema is renamed, only the right-hand
 * side of this alias needs to update.
 */
export type Account = components['schemas']['TenantResponse'];

/**
 * Directory user, returned from `GET /users`. Carries per-account memberships
 * (`accounts`) — each with the user's account-level role and an `appRoles`
 * slice keyed by `(app, role, organisationId)` that the FE filters on.
 */
export type User = components['schemas']['UserResponse'];

/** Per-account membership row embedded in `User.accounts`. */
export type AccountSummary = components['schemas']['AccountSummaryResponse'];

/** Per-org slot under an `AccountSummary`. */
export type OrganisationSummary = components['schemas']['OrganisationSummaryResponse'];

/**
 * A single `(app, role, organisationId)` grant. Carries the user's
 * functional-role assignments for that app:
 *   - `functionalRoles` — explicitly assigned, admin-editable in
 *     Settings → Users
 *   - `derivedFunctionalRoles` — read-only, computed at read-time on the
 *     BE (today: `Driver`, populated when a Driver-type Resource matches
 *     the user's email in this org). Effective capability = union of both.
 */
export type AppRoleSummary = components['schemas']['AppRoleSummaryResponse'];

/**
 * One row of the functional-role catalogue, returned by
 * `GET /users/functional-roles`. `key` is the stable PascalCase
 * identifier stored on `AppRoleSummary.functionalRoles`; the display
 * label comes from each app's i18n bundle.
 */
export type FunctionalRole = components['schemas']['FunctionalRoleResponse'];

/** Team row with embedded members, returned by `GET /teams`. */
export type Team = components['schemas']['TeamResponse'];

/** Slim member projection embedded in `Team` + `SiteWithMembers`. */
export type TeamMemberSummary = components['schemas']['TeamMemberSummaryResponse'];

/** POST `/teams` body. */
export type CreateTeamRequest = components['schemas']['CreateTeamRequest'];

/** PUT `/teams` body. */
export type UpdateTeamRequest = components['schemas']['UpdateTeamRequest'];

/** Single user-site assignment, returned by `GET /users/{id}/sites`. */
export type UserSite = components['schemas']['UserSiteResponse'];

/** PUT body for replacing a user's full Site assignment list. */
export type ReplaceUserSitesRequest = components['schemas']['ReplaceUserSitesRequest'];

/** PUT body for replacing a user's team memberships in an (org, app) scope. */
export type ReplaceUserTeamsRequest = components['schemas']['ReplaceUserTeamsRequest'];

/** Site row with embedded members, returned by `GET /sites/with-members`. */
export type SiteWithMembers = components['schemas']['SiteWithMembersResponse'];

/** PUT body for replacing a site's full member list. */
export type ReplaceSiteMembersRequest = components['schemas']['ReplaceSiteMembersRequest'];

/**
 * A pending account-membership invitation belonging to the calling user.
 * Returned by `GET /accountUsers/me/pending` — drives the post-login
 * prompt that lets a freshly-invited user accept (or, via the standard
 * delete flow, decline). Carries `accountUserId` which is the row to
 * accept against — `AccountSummary.id` is the *account* id and can't be
 * used for that call.
 */
export type PendingInvitation = components['schemas']['PendingInvitationResponse'];

/** Per-app slot inside a `PendingInvitation`. */
export type PendingAppRole = components['schemas']['PendingAppRoleResponse'];

/** POST `/users` body — invite/create a user with roles in one shot. */
export type CreateUserRequest = components['schemas']['CreateUserRequest'];

/** PUT `/users` body — update profile, platform role, functional roles. */
export type UpdateUserRequest = components['schemas']['UpdateUserRequest'];

/** Single organisation slot inside an account. */
export type AccountOrganisation = components['schemas']['TenantOrganisationDto'];

/** Per-app role inside an account. */
export type AccountAppRole = components['schemas']['TenantAppRoleDto'];

// ── Mobile users (Settings → Users & Access → Mobile Users tab) ──────────────
// Hand-shaped: these admin endpoints aren't in the generated OpenAPI schema yet.

/** Platform-identity status of a mobile (phone-authenticated) user. */
export type MobileUserStatusValue = 'Invited' | 'Active' | 'Suspended' | 'Deleted';

/** Descriptive field role for a mobile user. */
export type MobileUserRoleValue = 'Driver' | 'Mechanic' | 'Operative' | 'Supervisor' | 'Other';

/** Admin list row — `GET /mobile-users`. */
export interface MobileUserSummary {
  id: string;
  displayName: string;
  mobileNumberE164: string;
  status: MobileUserStatusValue;
  role: MobileUserRoleValue | null;
  identityVerified: boolean;
  lastSeenUtc: string | null;
  deviceCount: number;
  organisationId: string;
  appRole: string;
}

/** POST `/mobile-users/invite` body. */
export interface InviteMobileUserRequest {
  organisationId: string;
  mobileNumberE164: string;
  displayName: string;
  role?: MobileUserRoleValue | null;
  appKey?: AppNameValue;
  appRole?: AppRoleValue;
}

/** POST `/mobile-users/invite` response. */
export interface InviteMobileUserResponse {
  mobileUserId: string;
  status: string;
  maskedMobileNumber: string | null;
}

// ────────────────────────────────────────────────────────────────────────────
// Resources (fleet — drivers, trucks, rigids, trailers, other)
//
// TODO: regenerate the schema once the new BE is deployed and replace the
// hand-written types below with `components['schemas']['ResourceResponse']`,
// `components['schemas']['ResourceCertificationResponse']`, etc. The shapes
// match the BE DTOs exactly — the swap is mechanical (delete the local
// types, change the right-hand side of the alias).
// ────────────────────────────────────────────────────────────────────────────

/** Closed set of fleet resource types — drivers, vehicles, "other" (forklifts etc). */
export type ResourceTypeValue = 'Driver' | 'Truck' | 'Rigid' | 'Trailer' | 'Other';

/**
 * Closed set of certification kinds (matches `CertificationKey` enum on the BE).
 *
 * `Custom` is the catch-all for app-defined service-tag-style certs (TMS
 * service tags, Depot inspection items, etc). Custom rows additionally
 * carry a free-form `customLabel` and an owning `app` so each app's
 * Resources page can filter to its own slice.
 */
export type CertificationKeyValue =
  | 'Adr'
  | 'Cpc'
  | 'Mot'
  | 'Tachograph'
  | 'DriverCard'
  | 'Insurance'
  | 'FirstAid'
  | 'Hiab'
  | 'Forklift'
  | 'Psv'
  | 'Coshh'
  | 'OLicence'
  | 'Custom';

/** What the system does on cert expiry (mirrors `TagComplianceAction` on the BE). */
export type TagComplianceActionValue = 'None' | 'Warn' | 'Stop';

/** Resource-level compliance roll-up dot (mirrors `ComplianceIndicator` on the BE). */
export type ComplianceIndicatorValue = 'Red' | 'Yellow' | 'Green';

/**
 * Cert row attached to a resource. `attachmentUrl` is a short-lived SAS
 * URL minted by the BE on read — don't persist it client-side; refetch
 * when needed. Null when no file has been uploaded for this cert.
 */
export interface ResourceCertification {
  id: string;
  resourceId: string;
  certificationKey: CertificationKeyValue;
  /**
   * Free-form label, set only when `certificationKey === 'Custom'`. The
   * FE renders this in place of the catalogue label for the chip / row
   * name. Null for predefined certs.
   */
  customLabel: string | null;
  /**
   * Owning app for `Custom` certs (TMS / Depot / Expenses). Null for
   * predefined certs — those are cross-app. Each app's Resources page
   * filters custom certs to its own value before render.
   */
  app: AppNameValue | null;
  expiryDate: string | null; // ISO date string ("2027-03-01")
  warnDays: number | null;
  complianceAction: TagComplianceActionValue;
  note: string | null;
  attachmentUrl: string | null;
  attachmentFileName: string | null;
  attachmentContentType: string | null;
  attachmentFileSizeBytes: number | null;
  attachmentVersion: number;
}

/**
 * Resource read model returned from the BE. The compliance fields
 * (`complianceIndicator`, `isCompliant`, `nextExpiryDate`) are
 * recomputed BE-side on every cert change — trust them for grid sort /
 * filter without re-deriving on the FE.
 */
export interface Resource {
  id: string;
  organisationId: string;
  type: ResourceTypeValue;
  name: string;
  displayName: string | null;
  mobileNumber: string | null;
  mobileNumberVerified: boolean;
  email: string | null;
  reference: string | null;
  organisationEquipmentTypeId: string | null;
  status: BaseStatusValue;
  complianceIndicator: ComplianceIndicatorValue;
  isCompliant: boolean;
  nextExpiryDate: string | null;
  createdUtc: string;
  updatedUtc: string | null;
  certifications: ResourceCertification[];
}

/** POST `/directory/api/v1/Resources` body. */
export interface CreateResourceRequest {
  organisationId: string;
  type: ResourceTypeValue;
  name: string;
  displayName?: string | null;
  mobileNumber?: string | null;
  email?: string | null;
  reference?: string | null;
  organisationEquipmentTypeId?: string | null;
  status?: BaseStatusValue;
}

/** PUT `/directory/api/v1/Resources` body. */
export interface UpdateResourceRequest {
  id: string;
  type: ResourceTypeValue; // included so the BE validator's type-conditional rules apply
  name: string;
  displayName?: string | null;
  mobileNumber?: string | null;
  email?: string | null;
  reference?: string | null;
  organisationEquipmentTypeId?: string | null;
  status?: BaseStatusValue;
}

/**
 * PUT `/directory/api/v1/Resources/certifications` body — upserts a
 * single cert. For `certificationKey === 'Custom'`, both `customLabel`
 * and `app` are required (BE validator enforces, FE should mirror so
 * the form is rejected client-side too). For predefined keys, both
 * MUST be omitted / null — the BE validator rejects accidental sends.
 */
export interface SetResourceCertificationRequest {
  resourceId: string;
  certificationKey: CertificationKeyValue;
  customLabel?: string | null;
  app?: AppNameValue | null;
  expiryDate?: string | null;
  warnDays?: number | null;
  complianceAction?: TagComplianceActionValue;
  note?: string | null;
}

/**
 * One entry in the certification catalogue (per-key metadata layer
 * sitting alongside the closed `CertificationKeyValue` enum).
 * `appliesTo` lists lower-case `ResourceTypeValue` strings;
 * `countries` lists ISO 3166-1 alpha-2 codes (+ `"*"` sentinel for
 * "applies everywhere").
 */
export interface CertificationCatalogueEntry {
  key: string; // lower_snake_case form ("adr", "driver_card") matching CertificationKeyValue's PascalCase
  label: string;
  description: string;
  appliesTo: string[]; // lower-case ResourceTypeValue ("driver", "truck", ...)
  countries: string[]; // ISO 3166-1 alpha-2 + "*"
}

/** Bundled cert catalogue payload — same shape as the BE's `CertificationCatalogue` value object. */
export interface CertificationCatalogue {
  version: number;
  certifications: CertificationCatalogueEntry[];
}

// ────────────────────────────────────────────────────────────────────────────
// Parties (customers + suppliers)
//
// Same hand-written-types-until-schema-regenerated convention as Resources
// above. Field names + nullability mirror the BE PartyDto / PartyContactDto /
// PartyDocumentDto exactly.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Closed set of party kinds. `Both` is for parties that are simultaneously
 * a customer AND a supplier — they show on both list pages and the BE
 * filter (`?type=Customer`) returns them in either context.
 */
export type PartyTypeValue = 'Customer' | 'Supplier' | 'Both';

/**
 * Sub-classification for supplier parties. Null on Customer / Both parties.
 * `ShippingCompany` vs `ShippingAgency` distinguishes the operator of a
 * vessel (Stena Line, Irish Ferries) from the agency that resells crossings
 * on behalf of customers (e.g. Victor Tracy) — both can be the invoicing
 * counterpart on an order's ferry booking depending on who the customer
 * booked through.
 */
export type SupplierTypeValue =
  | 'Generic'
  | 'ShippingCompany'
  | 'ShippingAgency'
  | 'Haulier'
  | 'Fuel'
  | 'Garage'
  | 'Warehouse'
  | 'Other';

/** Lifecycle status for a party. `CeasedTrading` is terminal-but-undeleted. */
export type PartyStatusValue = 'Active' | 'Suspended' | 'CeasedTrading';

/** Invoice cadence for a party — drives downstream billing-run scheduling. */
export type BillingFrequencyValue = 'Weekly' | 'Monthly' | 'Quarterly' | 'Yearly';

/** Default settlement method on invoices issued to (or received from) the party. */
export type PaymentMethodValue = 'Cash' | 'Credit' | 'Other';

/**
 * Role/kind of a contact attached to a party. `Other` (= 0) is the BE's
 * fallback for unknown/legacy contact rows. The order in this union matches
 * the BE enum; the FE display order is set independently in i18n keys.
 */
export type ContactTypeValue =
  | 'Other'
  | 'Primary'
  | 'Secondary'
  | 'Billing'
  | 'Shipping'
  | 'Dispatcher'
  | 'Owner'
  | 'Admin'
  | 'ItTech';

/**
 * Network-link state for a party against another tenant on the platform.
 * `NotInvited` is the default for self-managed parties; the rest of the
 * lifecycle is driven by the (deferred) invitations subsystem.
 */
export type InvitationStatusValue =
  | 'NotInvited'
  | 'Pending'
  | 'Accepted'
  | 'Expired'
  | 'Revoked'
  | 'Rejected';

/** Document classification on a party document upload. */
export type PartyDocumentTypeValue =
  | 'Contract'
  | 'Finance'
  | 'Identity'
  | 'Insurance'
  | 'Tax'
  | 'Other';

/**
 * Party read model returned from the BE. The address fields are GUID
 * pointers — the addresses themselves are loaded via `getAddress(id)` or
 * the dedicated party-addresses endpoint (wave 3).
 *
 * `extraFields` is a JSON string (operator-defined custom fields, parsed
 * client-side when rendered). Surfaced unparsed here so the wire shape
 * matches the BE DTO exactly; consumers that don't care about extras can
 * ignore the field.
 */
export interface Party {
  id: string;
  organisationId: string;
  code: string;
  name: string;
  type: PartyTypeValue;
  /** Supplier sub-classification — null on Customer / Both parties. */
  subtype: SupplierTypeValue | null;
  status: PartyStatusValue;
  primaryAddressId: string;
  billingName: string;
  billingAddressId: string;
  billingFrequency: BillingFrequencyValue;
  paymentMethod: PaymentMethodValue;
  creditLimit: number;
  taxNumber: string | null;
  taxSchemeCountryCode: string;
  euVatStatement: boolean;
  currencyIsoCode: string;
  networkId: string | null;
  accountOwnerId: string | null;
  accountSystemName: string | null;
  accountSystemCode: string | null;
  extraFields: string | null;
  /**
   * Comma-joined ServiceTag names this party is tagged with. Null /
   * empty = no tags. Parsed by the FE on render with a split-on-comma
   * helper; the BE canonicalises (trim each entry, dedupe case-
   * insensitively) on every save so the wire shape is always tidy.
   */
  tags: string | null;
  /**
   * Primary-address summary fields surfaced inline so the parties list
   * grid can render Address / Country columns without an extra fetch
   * per row. Populated server-side from `Party.PrimaryAddress`; null
   * when the linked address is missing or the field itself is empty.
   * `primaryFullAddress` carries the Google-formatted single line that
   * replaced the legacy Address1/Address2 split.
   */
  primaryFullAddress: string | null;
  primaryAddressPostcode: string | null;
  primaryAddressCountryIsoCode: string | null;
  /**
   * Display name of the party's primary contact (preferring rows with
   * `ContactType.Primary`, otherwise the first non-deleted contact).
   * Null when the party has no contacts at all.
   */
  primaryContactName: string | null;
  linkedAccountId: string | null;
  linkedOrganisationId: string | null;
  linkedPartyId: string | null;
  invitationStatus: InvitationStatusValue;
  createdUtc: string;
  updatedUtc: string | null;
}

/** Contact row attached to a party (separate aggregate, separate endpoints). */
export interface PartyContact {
  id: string;
  organisationId: string;
  partyId: string;
  name: string;
  phoneNumber: string;
  email: string;
  notes: string | null;
  type: ContactTypeValue;
  createdUtc: string;
  updatedUtc: string | null;
}

/**
 * Document row attached to a party. `downloadUrl` is a short-lived SAS
 * URL minted by the BE on read — same lifecycle convention as cert
 * attachments. Don't persist; refetch when needed.
 */
export interface PartyDocument {
  id: string;
  organisationId: string;
  partyId: string;
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
  contentHash: string | null;
  blobName: string;
  containerName: string;
  description: string | null;
  type: PartyDocumentTypeValue;
  downloadUrl: string | null;
  createdUtc: string;
  updatedUtc: string | null;
}

/** POST `/directory/api/v1/parties` body. */
export interface CreatePartyRequest {
  organisationId: string;
  code: string;
  name: string;
  type: PartyTypeValue;
  /** Supplier sub-classification — only meaningful when `type=Supplier`. */
  subtype?: SupplierTypeValue | null;
  status?: PartyStatusValue;
  primaryAddressId: string;
  billingName: string;
  billingAddressId: string;
  billingFrequency?: BillingFrequencyValue;
  paymentMethod?: PaymentMethodValue;
  creditLimit?: number;
  taxNumber?: string | null;
  taxSchemeCountryCode?: string;
  euVatStatement?: boolean;
  currencyIsoCode?: string;
  accountOwnerId?: string | null;
  accountSystemName?: string | null;
  accountSystemCode?: string | null;
  extraFields?: string | null;
  /** Comma-joined ServiceTag names. Null / empty = no tags. */
  tags?: string | null;
}

/** PUT `/directory/api/v1/parties` body — full-payload replace. */
export interface UpdatePartyRequest {
  partyId: string;
  code: string;
  name: string;
  type: PartyTypeValue;
  /** Supplier sub-classification — only meaningful when `type=Supplier`. */
  subtype?: SupplierTypeValue | null;
  status?: PartyStatusValue;
  primaryAddressId: string;
  billingName: string;
  billingAddressId: string;
  billingFrequency?: BillingFrequencyValue;
  paymentMethod?: PaymentMethodValue;
  creditLimit?: number;
  taxNumber?: string | null;
  taxSchemeCountryCode?: string;
  euVatStatement?: boolean;
  currencyIsoCode?: string;
  accountOwnerId?: string | null;
  accountSystemName?: string | null;
  accountSystemCode?: string | null;
  extraFields?: string | null;
  /** Comma-joined ServiceTag names. Null / empty clears all tags. */
  tags?: string | null;
}

/** POST `/directory/api/v1/partyContacts` body. */
export interface CreatePartyContactRequest {
  partyId: string;
  name: string;
  phoneNumber: string;
  email: string;
  notes?: string | null;
  type: ContactTypeValue;
}

/** PUT `/directory/api/v1/partyContacts` body. Id field is `contactId`. */
export interface UpdatePartyContactRequest {
  contactId: string;
  name: string;
  phoneNumber: string;
  email: string;
  notes?: string | null;
  type: ContactTypeValue;
}

/**
 * PUT `/directory/api/v1/partyDocuments` body — only metadata edits
 * are allowed via PUT (description + type). The file itself is replaced
 * by uploading a new document; the BE keeps old blobs for audit.
 */
export interface UpdatePartyDocumentRequest {
  documentId: string;
  description?: string | null;
  type: PartyDocumentTypeValue;
}

/** Endpoints scoped to the Directory API (`/directory/api/v1/*`). */
export const directoryApi = {
  /** Smoke-test endpoint that returns `{ name, version, environment }`. */
  getApiInfo: () => request.get<ApiInfo>('/directory/api/v1/info/api-info'),

  /** GET a single organisation by id. */
  getOrganisation: (id: string) =>
    request.get<Organisation>(`/directory/api/v1/organisations/${id}`),

  /**
   * PUT the full organisation body. Server responds with the updated id
   * (string) wrapped in the `Result<T>` envelope; the platform `request.put`
   * helper unwraps to the bare id.
   */
  updateOrganisation: (body: UpdateOrganisationRequest) =>
    request.put<string>('/directory/api/v1/organisations', body),

  /**
   * Replace the organisation's additional-currency list atomically. The BE
   * normalises (uppercase + dedupe + drop base) and joins to comma-separated
   * for storage. Empty `codes` clears the field.
   */
  updateOrganisationAdditionalCurrencies: (
    organisationId: string,
    body: UpdateOrganisationAdditionalCurrenciesRequest,
  ) =>
    request.put<string>(
      `/directory/api/v1/organisations/${organisationId}/additional-currencies`,
      body,
    ),

  /**
   * Update the organisation's TMS order-reference-label map (used by the
   * TMS Order Details page to resolve the per-org names of
   * `Order.Reference1..4` slots + any long-tail entries). Dedicated
   * endpoint so the TMS surface can save labels without echoing the
   * entire org body back to the Directory.
   *
   * <p>
   * Body shape: `{ labelsJson: '{"Reference1":"Customer PO",...}' }` or
   * `{ labelsJson: null }` to clear. Anything other than a JSON object
   * is rejected with 422 by the BE.
   * </p>
   */
  updateOrganisationTmsOrderReferenceLabels: (
    organisationId: string,
    body: UpdateOrganisationTmsOrderReferenceLabelsRequest,
  ) =>
    request.put<string>(
      `/directory/api/v1/organisations/${organisationId}/tms-order-reference-labels`,
      body,
    ),

  /** GET a single address by id. */
  getAddress: (id: string) => request.get<Address>(`/directory/api/v1/addresses/${id}`),

  /**
   * Hydrate a specific set of addresses in one call (POST so the id list rides
   * in the body), scoped to an organisation. Used by the TMS Bookings worklist
   * to resolve origin/destination countries for the crossing test.
   */
  getAddressesByIds: (organisationId: string, ids: string[]) =>
    request.post<Address[]>('/directory/api/v1/addresses/by-ids', { organisationId, ids }),

  /**
   * GET addresses for an organisation. The optional `opts` bag narrows
   * the result on the server: `partyId` returns only the addresses
   * linked to that party (drives the per-party "Additional Addresses"
   * card grid); `addressTypes` filters by `AddressType` enum
   * (Locations page passes `['Task','Location']` to hide
   * Primary/Billing rows). Both filters can combine — omit them all
   * for the full org-scoped list.
   */
  getAddresses: (
    organisationId: string,
    opts?: { partyId?: string; addressTypes?: AddressTypeValue[] },
  ) => {
    const qs = new URLSearchParams({ organisationId });
    if (opts?.partyId) qs.set('partyId', opts.partyId);
    if (opts?.addressTypes?.length) {
      for (const t of opts.addressTypes) qs.append('addressTypes', t);
    }
    return request.get<Address[]>(`/directory/api/v1/addresses?${qs.toString()}`);
  },

  /**
   * POST a new address. Returns the new id; the caller is responsible for
   * linking it (e.g. PUT `/organisations` with `mainOfficeAddressId`).
   */
  createAddress: (body: CreateAddressRequest) =>
    request.post<string>('/directory/api/v1/addresses', body),

  /**
   * POST a port / terminal Location address from route catalogue data.
   * Skips Google Places geocoding — the BE validator waives `fullAddress`
   * and `placeId` when `addressType` is `Location`.
   */
  createLocationAddress: (body: CreateLocationAddressRequest) =>
    request.post<string>('/directory/api/v1/addresses', body),

  /** PUT the full address body. Returns the updated address id. */
  updateAddress: (body: UpdateAddressRequest) =>
    request.put<string>('/directory/api/v1/addresses', body),

  /** Soft-delete an address. */
  deleteAddress: (id: string) =>
    request.del<unknown>(`/directory/api/v1/addresses/${id}`),

  /**
   * POST a new site. Used by the Organisation settings page to seed a
   * "Main Office" site when creating the first address for a fresh org,
   * and by the Sites tab in Users & Access for ad-hoc site creation.
   * Returns the new site id.
   */
  createSite: (body: CreateSiteRequest) => request.post<string>('/directory/api/v1/sites', body),

  /**
   * PUT an existing site. Site id travels in the body (matches the BE
   * Mediator command shape). Used by the Sites tab to rename or
   * re-address a site. Returns the (unchanged) id.
   */
  updateSite: (body: UpdateSiteRequest) => request.put<string>('/directory/api/v1/sites', body),

  /**
   * DELETE a site by id. The BE soft-deletes (Site is `ISoftDelete`),
   * so the row is recoverable but no longer surfaces in queries. The
   * handler also blocks deletion if the site is referenced (members,
   * teams, etc.) via the standard FK-conflict path.
   */
  deleteSite: (id: string) => request.del<unknown>(`/directory/api/v1/sites/${id}`),

  /** GET all numbering sequences (Invoice, CreditNote, …) for an organisation. */
  getOrganisationSequences: (organisationId: string) =>
    request.get<OrganisationSequence[]>(
      `/directory/api/v1/organisationSequences?organisationId=${organisationId}`,
    ),

  /** POST a new sequence. Returns the new sequence id. */
  createOrganisationSequence: (body: CreateOrganisationSequenceRequest) =>
    request.post<string>('/directory/api/v1/organisationSequences', body),

  /**
   * PUT an existing sequence. Returns the same id. The server ignores
   * `currentNumber` once `isLocked === true`, and rejects un-locking with a
   * 409 Conflict.
   */
  updateOrganisationSequence: (body: UpdateOrganisationSequenceRequest) =>
    request.put<string>('/directory/api/v1/organisationSequences', body),

  /**
   * Atomically allocate the next number on a sequence and return the
   * formatted reference. Used by the TMS order screen to mint an order
   * number the moment the operator picks a customer — gaps in the
   * sequence are accepted (cancelled / unsaved drafts burn a number).
   *
   * The BE handler is non-idempotent on purpose: every call allocates.
   * Callers must NOT retry naively — wrap in TanStack Query mutations
   * and surface failures rather than silently looping.
   */
  consumeOrganisationSequence: (body: ConsumeOrganisationSequenceRequest) =>
    request.post<ConsumeOrganisationSequenceResponse>(
      '/directory/api/v1/organisationSequences/consume',
      body,
    ),

  /**
   * Upload (or replace) an organisation's logo. Sends `File` as multipart
   * form-data — matches the controller's `[FromForm] UploadOrganisationLogoRequest`
   * binding. Returns the fresh blob name + a short-lived SAS URL so the
   * caller can render the new image without a second round-trip.
   */
  uploadOrganisationLogo: (id: string, file: File) =>
    request.postMultipart<UploadOrganisationLogoResponse>(
      `/directory/api/v1/organisations/${id}/logo`,
      { File: file },
    ),

  /**
   * Clear an organisation's logo. Idempotent — succeeds even when no logo
   * is currently set. Server returns no body of interest, so we capture as
   * `unknown` and let the mutation discard it.
   */
  deleteOrganisationLogo: (id: string) =>
    request.del<unknown>(`/directory/api/v1/organisations/${id}/logo`),

  /**
   * GET tenants the signed-in account belongs to. Drives the TenantSwitcher
   * dropdown and seeds `setAvailableTenants(...)` so the role-resolution
   * helper in apps can pick the right organisation per tenant.
   */
  getAvailableAccounts: () => request.get<Account[]>('/directory/api/v1/accounts/available'),

  /**
   * GET every user in the caller's account. The Settings → Users screen
   * renders straight from this — fine while accounts stay small, but a
   * dedicated `/users?organisationId=&role=` query is the long-term
   * replacement.
   */
  getAccountUsers: () => request.get<User[]>('/directory/api/v1/users'),

  /** GET a single user by id (scoped to the caller's account). */
  getUserById: (id: string) =>
    request.get<User>(`/directory/api/v1/users/${id}`),

  /**
   * POST a new user. Either creates a fresh account user or attaches an
   * existing user (matched by email) and grants the supplied platform
   * role + optional functional roles for the current app. Returns the
   * (possibly pre-existing) user id.
   */
  createUser: (body: CreateUserRequest) =>
    request.post<string>('/directory/api/v1/users', body),

  /**
   * PUT an existing user. Updates profile fields + platform role +
   * functional roles in one shot. `functionalRoles: null` (or omitted)
   * leaves them unchanged; `[]` clears them.
   */
  updateUser: (body: UpdateUserRequest) =>
    request.put<string>('/directory/api/v1/users', body),

  /**
   * DELETE a user from the given organisation. Soft-delete on the BE —
   * leaves the underlying User record so audit trails resolve. Returns
   * 204 / no content.
   */
  deleteUser: (id: string, organisationId: string) =>
    request.del<unknown>(
      `/directory/api/v1/users/${id}?organisationId=${organisationId}`,
    ),

  /**
   * GET the functional-role catalogue. Pass `app` to filter to one app
   * (the Settings → Users picker calls with the current app); omit it
   * for a future cross-app admin screen.
   */
  getFunctionalRoles: (app?: AppNameValue) =>
    request.get<FunctionalRole[]>(
      app
        ? `/directory/api/v1/users/functional-roles?app=${app}`
        : '/directory/api/v1/users/functional-roles',
    ),

  /**
   * GET pending account-membership invitations for the caller. Drives
   * the post-login prompt — empty list means "nothing to do, dismiss
   * silently".
   */
  getPendingInvitations: () =>
    request.get<PendingInvitation[]>('/directory/api/v1/accountUsers/me/pending'),

  /**
   * POST accept the pending invitation identified by `accountUserId`.
   * Returns the now-Active `AccountSummary` so the FE can splice it into
   * the accounts cache without an extra round-trip. Idempotent — accepting
   * twice succeeds both times.
   */
  acceptInvitation: (accountUserId: string) =>
    request.post<AccountSummary>(
      `/directory/api/v1/accountUsers/${accountUserId}/accept`,
      {},
    ),

  // ── Teams (Settings → Users & Access → Teams tab) ─────────────────────

  /** GET teams in an organisation for an app. Members are inlined. */
  getTeams: (organisationId: string, app: AppNameValue) =>
    request.get<Team[]>(
      `/directory/api/v1/teams?organisationId=${organisationId}&app=${app}`,
    ),

  /** POST a new team. Returns the new team id. */
  createTeam: (body: CreateTeamRequest) =>
    request.post<string>('/directory/api/v1/teams', body),

  /** PUT atomic team update (name + description + site + members). Returns the same id. */
  updateTeam: (body: UpdateTeamRequest) =>
    request.put<string>('/directory/api/v1/teams', body),

  /** DELETE a team (soft-delete). */
  deleteTeam: (id: string) =>
    request.del<unknown>(`/directory/api/v1/teams/${id}`),

  // ── User-site assignment (cross-app) ─────────────────────────────────

  /** GET sites the user is currently assigned to. */
  getUserSites: (userId: string) =>
    request.get<UserSite[]>(`/directory/api/v1/users/${userId}/sites`),

  /**
   * PUT replace the user's full Site assignment list. Empty list = clear
   * all. The handler diffs current vs incoming.
   */
  replaceUserSites: (userId: string, body: ReplaceUserSitesRequest) =>
    request.put<unknown>(`/directory/api/v1/users/${userId}/sites`, body),

  /**
   * PUT replace the user's team memberships within an (org, app) scope.
   * Memberships in other scopes are left untouched.
   */
  replaceUserTeams: (userId: string, body: ReplaceUserTeamsRequest) =>
    request.put<unknown>(`/directory/api/v1/users/${userId}/teams`, body),

  // ── Sites tab (Users & Access) ───────────────────────────────────────

  /**
   * GET sites with embedded UserSite members for the Sites tab grid +
   * drawer. Distinct from `/sites` which returns the raw list without
   * memberships.
   */
  getSitesWithMembers: (organisationId: string) =>
    request.get<SiteWithMembers[]>(
      `/directory/api/v1/sites/with-members?organisationId=${organisationId}`,
    ),

  /**
   * PUT the site's full member list. Sites-direction mirror of
   * `replaceUserSites`. Empty list = clear all.
   */
  replaceSiteMembers: (siteId: string, body: ReplaceSiteMembersRequest) =>
    request.put<unknown>(`/directory/api/v1/sites/${siteId}/members`, body),

  // ── Mobile users (Settings → Users & Access → Mobile Users tab) ──────────

  /** GET mobile users in the caller's account for an (org, app). */
  getMobileUsers: (organisationId: string, app: AppNameValue = 'Safety') =>
    request.get<MobileUserSummary[]>(
      `/directory/api/v1/mobile-users?organisationId=${organisationId}&app=${app}`,
    ),

  /** POST allow-list (invite) a mobile user by phone number. */
  inviteMobileUser: (body: InviteMobileUserRequest) =>
    request.post<InviteMobileUserResponse>('/directory/api/v1/mobile-users/invite', body),

  /** POST suspend a mobile user — blocks authentication until reactivated. */
  suspendMobileUser: (id: string) =>
    request.post<unknown>(`/directory/api/v1/mobile-users/${id}/suspend`, {}),

  /** POST reactivate a suspended mobile user. */
  reactivateMobileUser: (id: string) =>
    request.post<unknown>(`/directory/api/v1/mobile-users/${id}/reactivate`, {}),

  /** DELETE revoke a mobile user's grant for one app/org (leaves other apps intact). */
  removeMobileUser: (id: string, organisationId: string, app: AppNameValue = 'Safety') =>
    request.del<unknown>(
      `/directory/api/v1/mobile-users/${id}?organisationId=${organisationId}&app=${app}`,
    ),

  /** GET all bank accounts for an organisation. */
  getOrganisationBankAccounts: (organisationId: string) =>
    request.get<OrganisationBankAccount[]>(
      `/directory/api/v1/organisationBankAccounts?organisationId=${organisationId}`,
    ),

  /** POST a new bank account. Returns the new bank account id. */
  createOrganisationBankAccount: (body: CreateOrganisationBankAccountRequest) =>
    request.post<string>('/directory/api/v1/organisationBankAccounts', body),

  /** PUT an existing bank account. Returns the same id. */
  updateOrganisationBankAccount: (body: UpdateOrganisationBankAccountRequest) =>
    request.put<string>('/directory/api/v1/organisationBankAccounts', body),

  /** DELETE a bank account by id. */
  deleteOrganisationBankAccount: (id: string) =>
    request.del<unknown>(`/directory/api/v1/organisationBankAccounts/${id}`),

  /** GET all tax rates for an organisation. */
  getOrganisationTaxRates: (organisationId: string) =>
    request.get<OrganisationTaxRate[]>(
      `/directory/api/v1/organisationTaxRates?organisationId=${organisationId}`,
    ),

  /** POST a new tax rate. Returns the new id. */
  createOrganisationTaxRate: (body: CreateOrganisationTaxRateRequest) =>
    request.post<string>('/directory/api/v1/organisationTaxRates', body),

  /** PUT an existing tax rate. Returns the same id. */
  updateOrganisationTaxRate: (body: UpdateOrganisationTaxRateRequest) =>
    request.put<string>('/directory/api/v1/organisationTaxRates', body),

  /** DELETE a tax rate by id. */
  deleteOrganisationTaxRate: (id: string) =>
    request.del<unknown>(`/directory/api/v1/organisationTaxRates/${id}`),

  /**
   * Seed an organisation with statutory tax-rate templates for a country.
   * Idempotent — re-seeding never duplicates. Returns the count of newly
   * created rows.
   */
  seedOrganisationTaxRates: (body: SeedOrganisationTaxRatesRequest) =>
    request.post<number>('/directory/api/v1/organisationTaxRates/seed-defaults', body),

  /** GET all nominal codes (chart of accounts) for an organisation. */
  getOrganisationNominals: (organisationId: string) =>
    request.get<OrganisationNominal[]>(
      `/directory/api/v1/organisationNominals?organisationId=${organisationId}`,
    ),

  /** POST a new nominal code. Returns the new id. */
  createOrganisationNominal: (body: CreateOrganisationNominalRequest) =>
    request.post<string>('/directory/api/v1/organisationNominals', body),

  /** PUT an existing nominal code. Returns the same id. */
  updateOrganisationNominal: (body: UpdateOrganisationNominalRequest) =>
    request.put<string>('/directory/api/v1/organisationNominals', body),

  /** DELETE a nominal code by id. */
  deleteOrganisationNominal: (id: string) =>
    request.del<unknown>(`/directory/api/v1/organisationNominals/${id}`),

  /**
   * GET the static equipment catalogue. Same payload for every tenant — cache
   * aggressively on the FE (catalogue keys are append-only / never reused).
   */
  getEquipmentCatalogue: () =>
    request.get<EquipmentCatalogue>('/directory/api/v1/equipmentCatalogue'),

  /** GET every active equipment-type selection (across both classes) for an organisation. */
  getOrganisationEquipmentTypes: (organisationId: string) =>
    request.get<OrganisationEquipmentType[]>(
      `/directory/api/v1/organisationEquipmentTypes?organisationId=${organisationId}`,
    ),

  /**
   * PUT the full selection for a SINGLE class (Trailer or Rigid). The server
   * diffs against existing rows: inserts new keys, restores soft-deleted
   * keys, and soft-deletes keys removed from the set. Returns the resulting
   * active selection for the requested class only.
   */
  replaceOrganisationEquipmentTypes: (body: ReplaceOrganisationEquipmentTypesRequest) =>
    request.put<OrganisationEquipmentType[]>(
      '/directory/api/v1/organisationEquipmentTypes',
      body,
    ),

  // ── Carriers & Routes ──────────────────────────────────────────────────

  /**
   * GET the static carrier catalogue. Same payload for every tenant — cache
   * aggressively (24h staleTime); catalogue keys are append-only.
   */
  getCarrierCatalogue: () =>
    request.get<CarrierCatalogue>('/directory/api/v1/carrierCatalogue'),

  /** GET every active carrier selection for an organisation. */
  getOrganisationCarriers: (organisationId: string) =>
    request.get<OrganisationCarrier[]>(
      `/directory/api/v1/organisationCarriers?organisationId=${organisationId}`,
    ),

  /**
   * PUT the org's full carrier selection. The server diffs against existing
   * rows: inserts (auto-creating a Supplier party where there's no match),
   * restores soft-deleted rows, and soft-deletes rows whose keys are no
   * longer in the set. Returns the resulting active selection.
   */
  replaceOrganisationCarriers: (body: ReplaceOrganisationCarriersRequest) =>
    request.put<OrganisationCarrier[]>(
      '/directory/api/v1/organisationCarriers',
      body,
    ),

  /**
   * GET the static shipping-routes catalogue. Same payload for every tenant
   * — cache aggressively (24h staleTime).
   */
  getRouteCatalogue: () =>
    request.get<RouteCatalogue>('/directory/api/v1/routeCatalogue'),

  /** GET every active route selection for an organisation. */
  getOrganisationRoutes: (organisationId: string) =>
    request.get<OrganisationRoute[]>(
      `/directory/api/v1/organisationRoutes?organisationId=${organisationId}`,
    ),

  /**
   * PUT the org's full route selection — diff-style semantics
   * (insert / restore / soft-delete).
   */
  replaceOrganisationRoutes: (body: ReplaceOrganisationRoutesRequest) =>
    request.put<OrganisationRoute[]>(
      '/directory/api/v1/organisationRoutes',
      body,
    ),

  // ── Resources ──────────────────────────────────────────────────────────

  /**
   * GET the certification catalogue. Tenant-agnostic; cache aggressively
   * (24h staleTime) — the catalogue is bundled JSON, only changes on
   * deploy.
   */
  getCertificationCatalogue: () =>
    request.get<CertificationCatalogue>('/directory/api/v1/certificationCatalogue'),

  /**
   * GET every resource in the given organisation, certifications eagerly
   * included. Sorted by type (drivers first) then name.
   */
  getResources: (organisationId: string) =>
    request.get<Resource[]>(
      `/directory/api/v1/resources?organisationId=${organisationId}`,
    ),

  /** GET a single resource by id (with certifications). */
  getResource: (id: string) =>
    request.get<Resource>(`/directory/api/v1/resources/${id}`),

  /** POST a new resource. Returns the freshly-created Resource (incl. id). */
  createResource: (body: CreateResourceRequest) =>
    request.post<Resource>('/directory/api/v1/resources', body),

  /** PUT a resource update (id in body — matches the rest of the Directory API). */
  updateResource: (body: UpdateResourceRequest) =>
    request.put<Resource>('/directory/api/v1/resources', body),

  /** Soft-delete a resource. Cert rows cascade-soft-delete BE-side. */
  deleteResource: (id: string) =>
    request.del<unknown>(`/directory/api/v1/resources/${id}`),

  /**
   * Upsert a single cert on a resource. Recomputes the resource's
   * compliance roll-up. Strict applies-to: rejects e.g. "MOT on driver".
   */
  setResourceCertification: (body: SetResourceCertificationRequest) =>
    request.put<ResourceCertification>(
      '/directory/api/v1/resources/certifications',
      body,
    ),

  /** Soft-delete a single cert off a resource. */
  deleteResourceCertification: (resourceId: string, certificationId: string) =>
    request.del<unknown>(
      `/directory/api/v1/resources/${resourceId}/certifications/${certificationId}`,
    ),

  /**
   * Upload (or replace) the file attached to a cert row. Sends `File`
   * as multipart form-data — matches the BE's
   * `[FromForm] UploadResourceCertificationAttachmentRequest`. Returns
   * the cert DTO with a fresh SAS URL ready for the FE to render.
   */
  uploadResourceCertificationAttachment: (
    resourceId: string,
    certificationId: string,
    file: File,
  ) =>
    request.postMultipart<ResourceCertification>(
      `/directory/api/v1/resources/${resourceId}/certifications/${certificationId}/attachment`,
      { File: file },
    ),

  /**
   * Clear a cert's attachment metadata. The blob stays in storage in
   * case of re-attach. Idempotent — succeeds even when no attachment is
   * currently set.
   */
  deleteResourceCertificationAttachment: (resourceId: string, certificationId: string) =>
    request.del<ResourceCertification>(
      `/directory/api/v1/resources/${resourceId}/certifications/${certificationId}/attachment`,
    ),

  // ── Parties (customers + suppliers) ────────────────────────────────────

  /**
   * GET every party in the organisation. Optional `type` filter narrows
   * to Customers (`type=Customer`) or Suppliers (`type=Supplier`); the BE
   * returns `Both` rows in either context (so a party flagged as both
   * shows on both list pages).
   */
  getParties: (organisationId: string, type?: PartyTypeValue) => {
    const qs = new URLSearchParams({ organisationId });
    if (type) qs.set('type', type);
    return request.get<Party[]>(`/directory/api/v1/parties?${qs.toString()}`);
  },

  /** GET a single party by id (basic-info shape — addresses/contacts/docs are separate calls). */
  getParty: (id: string) => request.get<Party>(`/directory/api/v1/parties/${id}`),

  /** POST a new party. Returns the new party id. */
  createParty: (body: CreatePartyRequest) =>
    request.post<string>('/directory/api/v1/parties', body),

  /** PUT a party update (id in body — matches the rest of the Directory API). */
  updateParty: (body: UpdatePartyRequest) =>
    request.put<string>('/directory/api/v1/parties', body),

  /** Soft-delete a party. Sub-entities (contacts, documents) cascade-soft-delete BE-side. */
  deleteParty: (id: string) => request.del<unknown>(`/directory/api/v1/parties/${id}`),

  // ── Party contacts ─────────────────────────────────────────────────────

  /** GET every contact attached to a party. */
  getPartyContacts: (partyId: string) =>
    request.get<PartyContact[]>(`/directory/api/v1/partyContacts?partyId=${partyId}`),

  /** GET a single contact by id. Rarely used directly; the list call covers most reads. */
  getPartyContact: (id: string) =>
    request.get<PartyContact>(`/directory/api/v1/partyContacts/${id}`),

  /** POST a new contact. Returns the new contact id. */
  createPartyContact: (body: CreatePartyContactRequest) =>
    request.post<string>('/directory/api/v1/partyContacts', body),

  /** PUT a contact update (id in body — matches the rest of the Directory API). */
  updatePartyContact: (body: UpdatePartyContactRequest) =>
    request.put<string>('/directory/api/v1/partyContacts', body),

  /** Soft-delete a contact. */
  deletePartyContact: (id: string) =>
    request.del<unknown>(`/directory/api/v1/partyContacts/${id}`),

  // ── Party documents ────────────────────────────────────────────────────

  /** GET every document attached to a party. */
  getPartyDocuments: (partyId: string) =>
    request.get<PartyDocument[]>(`/directory/api/v1/partyDocuments?partyId=${partyId}`),

  /** GET a single document by id (downloadUrl is a fresh SAS on every read). */
  getPartyDocument: (id: string) =>
    request.get<PartyDocument>(`/directory/api/v1/partyDocuments/${id}`),

  /**
   * Upload a new document. Sends `File` + metadata as multipart form-data
   * — matches the BE's `[FromForm] UploadPartyDocumentRequest` binding.
   * Returns the new document id.
   */
  uploadPartyDocument: (
    partyId: string,
    file: File,
    description: string | null,
    type: PartyDocumentTypeValue,
  ) =>
    request.postMultipart<string>('/directory/api/v1/partyDocuments', {
      PartyId: partyId,
      File: file,
      Description: description ?? '',
      Type: type,
    }),

  /**
   * PUT a document metadata edit (description + type only — the file
   * itself is replaced by uploading a new document; old blobs are kept
   * for audit).
   */
  updatePartyDocument: (body: UpdatePartyDocumentRequest) =>
    request.put<string>('/directory/api/v1/partyDocuments', body),

  /** Soft-delete a document. Blob stays in storage in case of restore. */
  deletePartyDocument: (id: string) =>
    request.del<unknown>(`/directory/api/v1/partyDocuments/${id}`),

  // ── Driver Roster ───────────────────────────────────────────────────────--

  /** GET the roster grid for an org + month (1-based month). */
  getRoster: (organisationId: string, year: number, month: number) =>
    request.get<RosterMonthDto>(
      `/directory/api/v1/roster?organisationId=${encodeURIComponent(organisationId)}&year=${year}&month=${month}`,
    ),

  /** GET the flat list of rostered drivers (config + name) for the Drivers grid. */
  getDriverRosterConfigs: (organisationId: string) =>
    request.get<RosterDriverRow[]>(
      `/directory/api/v1/roster/drivers?organisationId=${encodeURIComponent(organisationId)}`,
    ),

  /** Create or update a driver's roster config. */
  upsertDriverRosterConfig: (body: UpsertDriverRosterConfigRequest) =>
    request.put<DriverRosterConfigDto>('/directory/api/v1/roster/config', body),

  /** Add a working-day / leave block to a driver. */
  createRosterBlock: (body: CreateRosterBlockRequest) =>
    request.post<DriverRosterBlockDto>('/directory/api/v1/roster/blocks', body),

  /** Remove a roster block. */
  deleteRosterBlock: (id: string) =>
    request.del<unknown>(`/directory/api/v1/roster/blocks/${id}`),

  /** GET all roster groups for an org (for the picker + management). */
  getRosterGroups: (organisationId: string) =>
    request.get<RosterGroupDto[]>(
      `/directory/api/v1/rosterGroups?organisationId=${encodeURIComponent(organisationId)}`,
    ),

  /** Create a roster group. */
  createRosterGroup: (body: CreateRosterGroupRequest) =>
    request.post<RosterGroupDto>('/directory/api/v1/rosterGroups', body),

  /** Update a roster group. */
  updateRosterGroup: (body: UpdateRosterGroupRequest) =>
    request.put<RosterGroupDto>('/directory/api/v1/rosterGroups', body),

  /** Soft-delete a roster group. */
  deleteRosterGroup: (id: string) =>
    request.del<unknown>(`/directory/api/v1/rosterGroups/${id}`),
};

// ────────────────────────────────────────────────────────────────────────────
// Driver Roster DTOs (hand-written — wire format matches My.Directory roster
// endpoints; reconcile on the next OpenAPI regen).
// ────────────────────────────────────────────────────────────────────────────

export type RosterScheduleType = 'FullTime' | 'Casual';
export type RosterWorkingPattern =
  | 'MondayToFriday'
  | 'MondayToSaturday'
  | 'TuesdayToSaturday'
  | 'SundayToThursday'
  | 'TwoWeekRotation';
export type RosterBlockKind = 'Working' | 'Holidays' | 'RestPeriod' | 'SickDay' | 'OtherLeave';
export type RosterJobType = 'Trunk' | 'MultiDrop' | 'Shunter' | 'Ferry' | 'General';
/** Per-day grid status code. */
export type RosterDayStatus = 'W' | 'D/O' | 'H' | 'R' | 'S' | 'O';

export interface RosterDriverDto {
  resourceId: string;
  name: string;
  /** HH:mm, or null. */
  start: string | null;
  location: string | null;
  casual: boolean;
  /** Resource compliance roll-up — Red / Yellow / Green. */
  complianceIndicator: string;
  /** One status per day of the month. */
  days: RosterDayStatus[];
}

export interface RosterGroupRowsDto {
  groupId: string | null;
  groupName: string;
  sortOrder: number;
  drivers: RosterDriverDto[];
}

export interface RosterMonthDto {
  year: number;
  month: number;
  days: number;
  groups: RosterGroupRowsDto[];
}

export interface RosterGroupDto {
  id: string;
  name: string;
  sortOrder: number;
}

/** One row in the Drivers management grid — a driver's config flattened with name + group name. */
export interface RosterDriverRow {
  resourceId: string;
  name: string;
  rosterGroupId: string | null;
  groupName: string | null;
  homeDepotSiteId: string | null;
  startLocation: string | null;
  defaultStartTime: string | null;
  scheduleType: RosterScheduleType;
  workingPattern: RosterWorkingPattern;
  phaseEvenWeekStart: boolean;
  jobType: RosterJobType | null;
}

export interface DriverRosterConfigDto {
  id: string;
  resourceId: string;
  rosterGroupId: string | null;
  homeDepotSiteId: string | null;
  startLocation: string | null;
  /** HH:mm, or null. */
  defaultStartTime: string | null;
  scheduleType: RosterScheduleType;
  workingPattern: RosterWorkingPattern;
  phaseEvenWeekStart: boolean;
  jobType: RosterJobType | null;
}

export interface DriverRosterBlockDto {
  id: string;
  driverResourceId: string;
  /** yyyy-MM-dd. */
  fromDate: string;
  toDate: string;
  kind: RosterBlockKind;
  /** HH:mm, or null. */
  startTime: string | null;
  notes: string | null;
}

/** PUT `/roster/config` body. Times are `HH:mm:ss`, dates `yyyy-MM-dd`. */
export interface UpsertDriverRosterConfigRequest {
  organisationId: string;
  resourceId: string;
  rosterGroupId?: string | null;
  homeDepotSiteId?: string | null;
  startLocation?: string | null;
  defaultStartTime?: string | null;
  scheduleType: RosterScheduleType;
  workingPattern: RosterWorkingPattern;
  phaseEvenWeekStart: boolean;
  jobType?: RosterJobType | null;
}

/** POST `/roster/blocks` body. */
export interface CreateRosterBlockRequest {
  organisationId: string;
  driverResourceId: string;
  fromDate: string;
  toDate: string;
  kind: RosterBlockKind;
  startTime?: string | null;
  notes?: string | null;
}

export interface CreateRosterGroupRequest {
  organisationId: string;
  name: string;
  sortOrder: number;
}

export interface UpdateRosterGroupRequest {
  rosterGroupId: string;
  name: string;
  sortOrder: number;
}
